import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Lightweight click-to-expand image lightbox.
// Pass `images` (array of { src, label }) and `openIndex` (number | null).
// Wraps any thumbnail set — call openAt(i) from the thumbnail onClick.

export const useLightbox = (images) => {
  const [index, setIndex] = useState(null);
  const open = (i) => setIndex(i);
  const close = () => setIndex(null);
  const prev = useCallback(() => setIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length)), [images.length]);
  const next = useCallback(() => setIndex((i) => (i === null ? null : (i + 1) % images.length)), [images.length]);

  useEffect(() => {
    if (index === null) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handler);
    };
  }, [index, prev, next]);

  return { index, open, close, prev, next };
};

export const Lightbox = ({ images, index, close, prev, next }) => (
  <AnimatePresence>
    {index !== null && (
      <motion.div
        key="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-sm flex items-center justify-center"
        onClick={close}
        data-testid="lightbox-overlay"
      >
        {/* Close */}
        <button
          onClick={close}
          aria-label="Close"
          data-testid="lightbox-close"
          className="absolute top-5 right-5 w-12 h-12 flex items-center justify-center bg-black/40 hover:bg-ire-accent hover:text-black text-white border border-white/15 transition-all z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Prev */}
        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            data-testid="lightbox-prev"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/40 hover:bg-ire-accent hover:text-black text-white border border-white/15 transition-all z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}

        {/* Next */}
        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            data-testid="lightbox-next"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/40 hover:bg-ire-accent hover:text-black text-white border border-white/15 transition-all z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {/* Image */}
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-[95vw] max-h-[88vh] flex flex-col items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={images[index].full || images[index].src}
            alt={images[index].label}
            data-testid="lightbox-image"
            className="max-w-full max-h-[80vh] object-contain"
          />
          {images[index].label && (
            <div className="mt-4 flex items-baseline gap-4">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-ire-accent">
                {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
              </span>
              <span className="font-body text-sm text-white/85">{images[index].label}</span>
            </div>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
